import React, { useEffect, useRef, useState } from 'react';
import { useParagraphLinesStream } from '../../hooks/useParagraphLinesStream';

const PHASES = [
  'Reading the passage so far…',
  'Considering tone and pacing…',
  'Drafting continuation lines…',
  'Polishing pinyin and gloss…',
];
const PHASE_INTERVAL_MS = 3500;

/**
 * AIParagraphLinesSuggester — inline panel under the sentence list. Streams
 * candidate continuation sentences from the AI; teacher accepts or rejects
 * each. Accepted ones are appended to the parent draft via `onAccept(line)`.
 *
 * Pre-flight expectations:
 *   - title (string) — passage title for context (may be empty)
 *   - hskLevel (number) — target HSK level
 *   - existingSentences (ParagraphSentence[]) — what's already drafted
 *   - count (default 3)
 *   - onAccept(line), onClose() callbacks
 */
function AIParagraphLinesSuggester({
  title,
  hskLevel,
  existingSentences,
  count = 3,
  onAccept,
  onClose,
}) {
  const { status, lines, thinking, error, submit, cancel } = useParagraphLinesStream();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [rejectedIdx, setRejectedIdx] = useState(new Set());

  // StrictMode-safe single submission, same pattern as AIExampleSuggester.
  const didStartRef = useRef(false);
  useEffect(() => {
    if (didStartRef.current) return;
    didStartRef.current = true;
    submit({
      title,
      hskLevel: Number(hskLevel) || 1,
      existingSentences: existingSentences.map((s) => ({
        zh: s.zh || '',
        py: s.py || '',
        en: s.en || '',
        hint: s.hint || '',
      })),
      count,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'streaming') return undefined;
    const id = setInterval(() => {
      setPhaseIdx((i) => Math.min(i + 1, PHASES.length - 1));
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status]);

  useEffect(() => {
    if (thinking && thinking.length > 0) {
      setPhaseIdx((i) => (i === 0 ? 1 : i));
    }
  }, [thinking]);

  const handleAccept = (idx) => {
    onAccept(lines[idx]);
    setRejectedIdx((prev) => new Set([...prev, idx]));
  };
  const handleReject = (idx) => {
    setRejectedIdx((prev) => new Set([...prev, idx]));
  };

  const visibleLines = lines
    .map((ln, idx) => ({ ln, idx }))
    .filter(({ idx }) => !rejectedIdx.has(idx));

  return (
    <div
      style={{
        marginTop: 12,
        padding: 16,
        background: 'var(--paper-2, #f7f4ed)',
        border: '1px solid var(--rule, #e3dfd2)',
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {status === 'streaming' && (
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--ink-2, #6e6a5e)',
                animation: 'aiPulse 1.4s ease-in-out infinite',
              }}
            />
          )}
          <strong style={{ fontSize: 13 }}>
            {status === 'streaming'
              ? 'AI suggesting lines…'
              : status === 'error'
                ? 'AI suggestion failed.'
                : 'AI suggestions'}
          </strong>
          {status === 'streaming' && (
            <span
              key={PHASES[phaseIdx]}
              className="ai-phase"
              style={{
                fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                fontSize: 11,
                color: 'var(--ink-3, #8b8779)',
                letterSpacing: '0.06em',
              }}
            >
              {PHASES[phaseIdx]}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { cancel(); onClose(); }}
          style={{
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            color: 'var(--ink-3, #8b8779)',
            fontSize: 12,
          }}
        >
          Close
        </button>
      </div>

      {error && (
        <p style={{ color: 'var(--vermillion, #c0392b)', fontSize: 12 }}>{error}</p>
      )}

      {visibleLines.length === 0 && status !== 'streaming' && !error && (
        <p style={{ fontSize: 12, color: 'var(--ink-3, #8b8779)' }}>
          No suggestions left — close the panel or try again.
        </p>
      )}

      {visibleLines.map(({ ln, idx }) => (
        <div
          key={idx}
          style={{
            background: 'white',
            border: '1px solid var(--rule, #e3dfd2)',
            borderRadius: 6,
            padding: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ fontFamily: 'var(--font-han)', fontSize: 16, marginBottom: 4 }}>
            {ln.zh}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-2, #6e6a5e)',
              fontStyle: 'italic',
              marginBottom: 4,
            }}
          >
            {ln.py}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2, #6e6a5e)', marginBottom: ln.hint ? 4 : 8 }}>
            {ln.en}
          </div>
          {ln.hint && (
            <div style={{ fontSize: 11, color: 'var(--ink-3, #8b8779)', marginBottom: 8 }}>
              hint: {ln.hint}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => handleReject(idx)}
              style={{
                fontSize: 11,
                padding: '4px 10px',
                background: 'transparent',
                border: '1px solid var(--rule, #e3dfd2)',
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--ink-3, #8b8779)',
              }}
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => handleAccept(idx)}
              style={{
                fontSize: 11,
                padding: '4px 10px',
                background: 'var(--ink, #2a2722)',
                border: 0,
                borderRadius: 4,
                cursor: 'pointer',
                color: 'white',
              }}
            >
              Accept ↓
            </button>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes aiPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.9); }
          50%      { opacity: 1;   transform: scale(1.15); }
        }
        .ai-phase {
          animation: aiPhaseFade 500ms ease-out both;
        }
        @keyframes aiPhaseFade {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default AIParagraphLinesSuggester;
